// src/App.js
import React, { useState, useEffect } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useNavigate
} from 'react-router-dom';

import Login from './components/Login';

import Dashboard from './pages/Dashboard';
import ApplicationProtect from './pages/ApplicationProtect';
import FirewallRules from './pages/FirewallRules';
import Analytics from './pages/Analytics';
import Attacks from './pages/Attacks';
import Layer7Attacks from './pages/Layer7Attacks';
import VulnerabilityManager from './pages/VulnerabilityManager';
import TrafficAnalyzer from './pages/TrafficAnalyzer';
import Account from './pages/Account';

function AppContent() {
  const [user, setUser] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    // 1. On mount, check localStorage for user + expiration
    const storedUser = localStorage.getItem('user');
    const expirationRaw = localStorage.getItem('expiration');
    if (storedUser && expirationRaw) {
      const expiration = new Date(expirationRaw);
      if (new Date() < expiration) {
        setUser(JSON.parse(storedUser));
      }
    }
  }, []);

  // ---------------------------------------------------------------------
  // 2) "handleLogin" function remains the same
  // ---------------------------------------------------------------------
  const handleLogin = (userObject) => {
    // userObject = { username, token, fullname }
    setUser(userObject);

    localStorage.setItem('user', JSON.stringify(userObject));
    const expirationTime = new Date(Date.now() + 14 * 24 * 60 * 60 * 1000);
    localStorage.setItem('expiration', expirationTime.toISOString());

    navigate('/');
  };

  // ---------------------------------------------------------------------
  // 3) Check if token is expired right away
  // ---------------------------------------------------------------------
  const isLoginExpired = () => {
    const expirationRaw = localStorage.getItem('expiration');
    if (!expirationRaw) return true;
    return new Date() > new Date(expirationRaw);
  };

  // ---------------------------------------------------------------------
  // 4) New useEffect to "Ping" the server if we have a user
  // ---------------------------------------------------------------------
  useEffect(() => {
    if (!user) return; // nothing to check
    if (isLoginExpired()) {
      // If the token is already expired, log them out
      localStorage.removeItem('user');
      localStorage.removeItem('expiration');
      setUser(null);
      navigate('/login');
      return;
    }

    // "Ping" your backend with the token
    fetch('https://backend.hybridprotect.hybridhosting.co/api/v1/account', {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${user.token}` // your token
      }
    })
      .then(res => {
        // If token is invalid or expired -> 401 -> force logout
        if (res.status === 401) {
          localStorage.removeItem('user');
          localStorage.removeItem('expiration');
          setUser(null);
          navigate('/login');
          return null;
        }
        if (!res.ok) {
          throw new Error(`Auth check failed: ${res.status}`);
        }
        return res.json();
      })
      .then(data => {
        if (data) {
          // data is the user’s account or something. If success, do nothing else
          console.log('Auth check success: token is valid.');
        }
      })
      .catch(err => {
        console.error('Auth check error:', err);
        // If there's an error (like network issues),
        // optionally decide to log out or keep them.
      });
  }, [user, navigate]);

  // ---------------------------------------------------------------------
  // 5) Decide which view to show:
  // ---------------------------------------------------------------------
  if (!user || isLoginExpired()) {
    // If not logged in or token expired, show login
    return <Login onLogin={handleLogin} />;
  }

  // Otherwise, show the normal routes
  return (
    <Routes>
      <Route path="/" element={<Dashboard />} />
      <Route path="/application-protect" element={<ApplicationProtect />} />
      <Route path="/firewall-rules" element={<FirewallRules />} />
      <Route path="/analytics" element={<Analytics />} />
      <Route path="/attacks" element={<Attacks />} />
      <Route path="/layer7-attacks" element={<Layer7Attacks />} />
      <Route path="/vulnerability-manager" element={<VulnerabilityManager />} />
      <Route path="/traffic-analyzer" element={<TrafficAnalyzer />} />
      <Route path="/account" element={<Account />} />
      <Route path="/login" element={<Login onLogin={handleLogin} />} />
    </Routes>
  );
}

export default function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}